export type InlineHighlightSegment = {
  kind: 'text' | 'bold' | 'primary'
  text: string
}

/**
 * `**text**` marks bold copy and `==text==` marks copy in the primary colour.
 * The content must be at least one character, and `.` does not match a newline,
 * so a marker never spans lines.
 */
const HIGHLIGHT_MARKER = /\*\*(.+?)\*\*|==(.+?)==/

/**
 * `String.split` with capture groups returns the parts in a fixed rhythm: the
 * text before a match, then the bold group, then the primary group, and so on,
 * ending with the text after the last match. The group that did not match comes
 * back as `undefined`.
 */
const kindAt = (index: number): InlineHighlightSegment['kind'] => {
  switch (index % 3) {
    case 1:
      return 'bold'
    case 2:
      return 'primary'
    default:
      return 'text'
  }
}

/**
 * Splits one line of dashboard banner copy into plain and highlighted segments.
 *
 * Marketing authors emphasis as plain-text markers because the body is stored
 * as text, never as HTML. The caller renders each segment as a React text node,
 * so the copy cannot inject markup.
 *
 * Behaviour at the edges:
 * - A marker with no closing partner stays in the copy as literal text.
 * - An empty pair (`****` or `====`) is not a highlight and stays literal.
 * - Markers do not nest. A marker inside another highlight stays literal text
 *   inside that highlight.
 * - A line with no markers comes back as one `text` segment, unchanged.
 * - An empty line comes back as no segments.
 */
export function parseInlineHighlights(line: string): InlineHighlightSegment[] {
  return line.split(HIGHLIGHT_MARKER).flatMap((part: string | undefined, index) => {
    // `undefined` is the group that did not match; `''` is the empty text
    // between two adjacent highlights. Neither is copy.
    if (part === undefined || part === '') {
      return []
    }

    return [{ kind: kindAt(index), text: part }]
  })
}
